import {
	PnLTimeSeriesDataPoint,
	PnlTimePeriodOption,
} from 'src/@types/performance';
import { PnlChartType, ZeroKey } from 'src/@types/performance';
import { timeAgo, periodTimeSize, periodOffset } from './dateTime';

export const applyTimeFilter = (
	data: PnLTimeSeriesDataPoint[],
	filter: PnlTimePeriodOption,
	useOffset = true
) => {
	return data.filter(
		(point) =>
			timeAgo(point.date) <=
			periodTimeSize(filter) + (useOffset ? periodOffset(filter) : 0)
	);
};

/* yes I just invented the word "cumulatize" (to make a timeseries into a cumulative timeseries)*/
export const cumulatizePnlPoints = (
	points: PnLTimeSeriesDataPoint[],
	zeroKey: ZeroKey
) => {
	if (!points || points.length === 0) return points;

	const initialValue = points[0][zeroKey];

	return points.map((point, index) => ({
		...point,
		[zeroKey]:
			points.slice(0, index).reduce((a, b) => a + (b[zeroKey] ?? 0), 0) -
			(index === 0 ? 0 : (initialValue ?? 0)),
	}));
};

export const addInitialZero = (
	pnlHistory: PnLTimeSeriesDataPoint[],
	period: PnlTimePeriodOption
) => {
	const initialPoint = pnlHistory?.[0];

	if (!initialPoint) return pnlHistory;

	const expectedPointTs =
		period === 'all'
			? initialPoint.date.getTime() - 1000 * 60 * 60
			: Date.now() - periodOffset(period) - periodTimeSize(period);

	if (initialPoint.date.getTime() > expectedPointTs) {
		return [
			{
				date: new Date(expectedPointTs),
				totalPnl: 0,
				spotPnl: 0,
				perpPnl: 0,
				accountValue: 0,
				perpRoi: 0,
				spotRoi: 0,
				referralRewards: 0,
				referralVolume: 0,
				referralCount: 0,
				hideTooltip: true,
			},
			{
				date: new Date(initialPoint.date.getTime() - 1000 * 60 * 60),
				totalPnl: 0,
				spotPnl: 0,
				perpPnl: 0,
				accountValue: 0,
				perpRoi: 0,
				spotRoi: 0,
				referralRewards: 0,
				referralVolume: 0,
				referralCount: 0,
				hideTooltip: true,
			},
			...pnlHistory,
		];
	}

	return pnlHistory;
};

/**
 * Add "zeroes" to the data where the values cross between positive and negative
 * @param data
 * @param zeroesKey
 * @returns
 */
export const addZeroes = (
	data: PnLTimeSeriesDataPoint[],
	zeroesKey?: keyof Pick<PnLTimeSeriesDataPoint, PnlChartType>
) => {
	const newData: PnLTimeSeriesDataPoint[] = [];

	data.forEach((dataPoint, index) => {
		const thisPoint = dataPoint;
		const nextPoint = data[index + 1];

		const pointsToAdd: PnLTimeSeriesDataPoint[] = [dataPoint];

		// skip last datapoint
		if (index === data.length - 1) {
			newData.push(...pointsToAdd);
			return;
		}

		if (
			zeroesKey &&
			thisPoint[zeroesKey] &&
			nextPoint[zeroesKey] &&
			Math.sign(thisPoint[zeroesKey]) !== Math.sign(nextPoint[zeroesKey]) &&
			Math.sign(thisPoint[zeroesKey]) !== 0 &&
			Math.sign(nextPoint[zeroesKey]) !== 0
		) {
			const timeDiff = nextPoint.date.getTime() - thisPoint.date.getTime();

			const YDiff = nextPoint[zeroesKey] - thisPoint[zeroesKey];

			const YDiffRatio = Math.abs(thisPoint[zeroesKey] / YDiff);

			const zeroTimeDiff = timeDiff * Math.abs(YDiffRatio);

			const newPoint = {
				date: new Date(thisPoint.date.getTime() + zeroTimeDiff),
				accountValue:
					(thisPoint.accountValue ?? 0 + (nextPoint.accountValue ?? 0)) / 2,
				totalPnl: (thisPoint.totalPnl ?? 0 + (nextPoint.totalPnl ?? 0)) / 2,
				referralRewards:
					(thisPoint.referralRewards ?? 0 + (nextPoint.referralRewards ?? 0)) /
					2,
				referralVolume:
					(thisPoint.referralVolume ?? 0 + (nextPoint.referralVolume ?? 0)) / 2,
				referralCount:
					(thisPoint.referralCount ?? 0 + (nextPoint.referralCount ?? 0)) / 2,
				hideTooltip: true,
				[zeroesKey]: 0,
			};

			pointsToAdd.push(newPoint);
		}

		newData.push(...pointsToAdd);
	});

	return newData;
};
